import { DocumentDuplicateIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';

import { IconButton } from '@/components/core/IconButton';
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@/components/core/Popover';
import { toPixelColumnSize } from '@/services/utils';

import { MonitoringDataType } from './monitor.types';

const SummaryColumn = ({ value }: { value: string }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="tw-relative tw-z-0">
            <div className="tw-whitespace-nowrap tw-truncate tw-pr-10">
                {value}
            </div>
            <Popover open={open}>
                <PopoverTrigger
                    asChild
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)} >
                    <div className="tw-absolute tw-right-0 tw-top-0">
                        <IconButton icon={InformationCircleIcon} variant="default" />
                    </div>
                </PopoverTrigger>
                <PopoverPortal>
                    <PopoverContent
                        side="left"
                        sideOffset={-8}
                        className="tw-w-full tw-max-w-[60vw] tw-max-h-[50vh] tw-p-6 tw-overflow-y-auto tw-whitespace-pre-wrap"
                        onMouseEnter={() => setOpen(true)}
                        onMouseLeave={() => setOpen(false)}>
                        <span autoFocus={true}>{value}</span>
                        <div className="tw-absolute tw-right-3 tw-top-0">
                            <IconButton
                                icon={DocumentDuplicateIcon}
                                variant="default"
                                onClick={() => {
                                    navigator.clipboard.writeText(value);
                                    setOpen(false);
                                }} />
                        </div>
                    </PopoverContent>
                </PopoverPortal>
            </Popover>
        </div>
    );
};

export const columns: (t: (key: string) => string) => ColumnDef<MonitoringDataType>[]
    = (t) => ([
        {
            accessorKey: 'timestamp',
            size: toPixelColumnSize(0.25),
            minSize: 150,
            header: t('DATE'),
            cell: ({ row }) => (
                <div className="tw-whitespace-nowrap">
                    {new Date(row.getValue('timestamp')).toLocaleString()}
                </div>
            ),
        },
        {
            accessorKey: 'module',
            header: t('MODULE'),
            size: toPixelColumnSize(0.25),
            minSize: 150,
        },
        {
            accessorKey: 'description',
            header: t('SUMMARY'),
            size: toPixelColumnSize(0.50),
            minSize: 150,
            cell: ({ row }) => <SummaryColumn value={row.getValue('description')} />,
        },
    ]);
